import { Query, useQuery } from '@tanstack/react-query';
import { getAllFunnelContactsNew } from 'API/linkedin-funnel-search';
import { AxiosError } from 'axios';
import { FunnelContact } from 'features/co-pilot/co-pilot.types';
import { useDebounce } from 'hooks/use-debounce';
import { useUserData } from 'hooks/use-user-data';
import { useEffect, useMemo } from 'react';
import useErrorStore, { CustomErrorClass } from 'stores/error-store';
import { ContactFilter } from '../../campaign-builder.types';

interface ContactsResponse {
  funnelContacts: FunnelContact[];
  total: number;
  isProcessing: boolean;
  isProcessingProfileAnalysis?: boolean;
}

interface Params {
  aiJudgement?: string;
  filter?: ContactFilter;
  limit?: number;
  offset?: number;
  q?: string;
}

const DEBOUNCE_DELAY = 500;
const STALE_TIME = 2 * 60 * 1000;
const REFRESH_INTERVAL = 10 * 1000;

const useFinalContacts = (
  campaignId: string | undefined = '',
  allowed: boolean,
  contactsPage: number,
  contactsSearchParam: string,
  contactsPerPage: number,
  resetContactsPage: () => void
) => {
  const { token } = useUserData();
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const debouncedContactsSearchParam = useDebounce(contactsSearchParam, DEBOUNCE_DELAY);

  const params: Params = useMemo(() => {
    return {
      aiJudgement: undefined,
      filter: ContactFilter.IS_ACTIVE,
      limit: contactsPerPage,
      offset: contactsPerPage * (contactsPage - 1),
      q: debouncedContactsSearchParam || undefined,
    };
  }, [contactsPage, debouncedContactsSearchParam]);

  const { data, isLoading, isError, error } = useQuery<ContactsResponse>({
    queryKey: ['finalContacts', token, campaignId, params],
    queryFn: ({ signal }) => getAllFunnelContactsNew(campaignId, params, signal),
    enabled: !!token && !!campaignId && !!params && allowed,
    staleTime: STALE_TIME,
    refetchInterval: (query: Query<ContactsResponse, Error>) =>
      query.state.data?.isProcessing || query.state.data?.isProcessingProfileAnalysis ? REFRESH_INTERVAL : false,
  });

  useEffect(() => {
    if (isError) {
      const customError = new CustomErrorClass(error as AxiosError);
      handleOpenErrorDialog(customError, customError.request, customError.requestType);
    }
  }, [isError, error]);

  useEffect(() => {
    resetContactsPage();
  }, [debouncedContactsSearchParam]);

  return {
    finalContacts: data?.funnelContacts || [],
    totalFinalContacts: data?.total || 0,
    finalContactsProcessing: data?.isProcessing || false,
    profileAnalysisProcessing: data?.isProcessingProfileAnalysis || false,
    isLoading,
    isError,
    error,
  };
};

export default useFinalContacts;
